import type {
  GamePlayerProjectionTrading,
  GameTeamPlayerProjectionObject
} from '@library/types/trading';
import {
  determineTeamHeaders,
  getModifiedTradingDashProjections,
  getTradingGameStartTimes
} from '@bo-utils/trading';
import { useCallback, useEffect } from 'react';
import { ConfirmRemoveProjectionsModal } from '@bo-components/trading/projections/ConfirmRemoveProjectionsModal';
import { type ConfirmationModalProps } from '@library/components/modals/ConfirmationModal';
import type { LeagueGames } from '@library/types/games';
import type { LeagueInfo } from '@library/types/leagues';
import { MessageBanner } from '@library/components/banners/MessageBanner';
import type { PlayerDescriptors } from '@library/types/player';
import { SubmitResetBar } from '@library/components/SubmitResetBar';
import { TradingTeam } from '@bo-components/trading/games/TradingTeam';
import { getErrorMessage } from '@bo-utils/common';
import { useImmer } from 'use-immer';
import { useRemovalWarnings } from '@bo-hooks/playerModal/useRemovalWarnings';
import { useTradingActions } from '@bo-hooks/trading/useTradingActions';

export interface TradingGamesProps {
  bulkLeagueIdGroups: Array<Array<string>>;
  disabledFilters: boolean;
  initialTradingGames: GameTeamPlayerProjectionObject;
  leagueInfo: LeagueInfo;
  games: LeagueGames;
  handleSelectPlayerModal: ({
    gameStartDateTime,
    newPlayerDescriptors,
    newProjection
  }: {
    gameStartDateTime: string;
    newPlayerDescriptors: PlayerDescriptors;
    newProjection: GamePlayerProjectionTrading;
  }) => void;
  handleShowAutoTradeDrawer: ({
    gameStartDateTime,
    newPlayerDescriptors,
    newProjection
  }: {
    gameStartDateTime: string;
    newPlayerDescriptors: PlayerDescriptors;
    newProjection: GamePlayerProjectionTrading;
  }) => void;
  setConfirmationModalProps: (
    props: Omit<ConfirmationModalProps, 'onCancelModal'> | false
  ) => void;
  setDisabledFilters: (disableFilters: boolean) => void;
  updateConfirmationModalProp: (
    updateFn: (
      prevProps: Omit<ConfirmationModalProps, 'onCancelModal'>
    ) => Omit<ConfirmationModalProps, 'onCancelModal'>
  ) => void;
  isDisplayTimeVisible?: boolean;
}

export const TradingGames = ({
  bulkLeagueIdGroups,
  disabledFilters,
  initialTradingGames,
  leagueInfo,
  games,
  handleSelectPlayerModal,
  handleShowAutoTradeDrawer,
  setConfirmationModalProps,
  setDisabledFilters,
  updateConfirmationModalProp,
  isDisplayTimeVisible = false
}: TradingGamesProps) => {
  const [changes, setChanges] = useImmer<
    Array<Partial<GamePlayerProjectionTrading>>
  >([]);

  const {
    isPublishProjectionsError,
    isPublishProjectionsUninitialized,
    handlePublishProjections,
    publishProjectionsError,
    resetCreateProjections
  } = useTradingActions();

  const updateProjection = ({
    change,
    initialProjection
  }: {
    change: Partial<GamePlayerProjectionTrading>;
    initialProjection: GamePlayerProjectionTrading;
  }) => {
    const { projectionId, projectionValue, autoTrade, inGame, gameModeIds } =
      initialProjection;
    const {
      projectionValue: changedProjectionValue,
      autoTrade: changedAutoTrade,
      inGame: changedInGame,
      gameModeIds: changedGameModeIds
    } = change;

    setChanges((draft) => {
      const affectedProjectionIndex = draft.findIndex(
        (projection) => projection.projectionId === projectionId
      );

      if (affectedProjectionIndex === -1) {
        draft.push({
          projectionId,
          ...change
        });

        return;
      }

      if (
        changedProjectionValue &&
        projectionValue === changedProjectionValue &&
        autoTrade === changedAutoTrade &&
        inGame === changedInGame &&
        gameModeIds === changedGameModeIds
      ) {
        draft.splice(affectedProjectionIndex, 1);
      } else {
        draft[affectedProjectionIndex] = {
          ...draft[affectedProjectionIndex],
          ...change
        };
      }
    });
  };

  const errorMessage = isPublishProjectionsError
    ? `Error: ${getErrorMessage({
        isPublishProjectionsError,
        publishProjectionsError
      })}`
    : '';

  const closeErrorMessage = useCallback(() => {
    if (!isPublishProjectionsUninitialized) {
      resetCreateProjections();
    }
  }, [isPublishProjectionsUninitialized, resetCreateProjections]);

  const updatedProjectionList = getModifiedTradingDashProjections({
    initialTradingGames,
    updatedTradingGames: changes
  });

  const onNoWarnings = useCallback(() => {
    handlePublishProjections(leagueInfo.id, updatedProjectionList).catch(
      () => {}
    );
    setChanges([]);
  }, [
    handlePublishProjections,
    leagueInfo.id,
    setChanges,
    updatedProjectionList
  ]);

  const {
    processWarnings,
    removalWarnings,
    handleCancelRemoval,
    handleConfirmRemoval
  } = useRemovalWarnings({
    changes,
    projections: updatedProjectionList,
    onNoWarnings
  });

  const totalChanges = updatedProjectionList?.length;
  const showSubmitResetBar = totalChanges > 0;

  useEffect(() => {
    const hasChanges = !!totalChanges;

    if (disabledFilters !== hasChanges) {
      setDisabledFilters(hasChanges);
    }

    if (totalChanges) {
      closeErrorMessage();
    }
  }, [closeErrorMessage, disabledFilters, setDisabledFilters, totalChanges]);

  const handleSubmit = useCallback(() => {
    processWarnings();
  }, [processWarnings]);

  const handleReset = useCallback(() => {
    setChanges([]);
  }, [setChanges]);

  return (
    <>
      <ConfirmRemoveProjectionsModal
        removalWarnings={removalWarnings}
        handleCancelRemoval={handleCancelRemoval}
        handleConfirmRemoval={handleConfirmRemoval}
      />
      <div className="pt-4" data-testid="TradingGames">
        {showSubmitResetBar && (
          <div className="sticky top-0 z-40">
            <SubmitResetBar
              handlePublish={handleSubmit}
              handleReset={handleReset}
              prefix={
                <span className="text-soClean-100 pl-5 text-sm">{`You've edited ${totalChanges} Projection${
                  totalChanges > 1 ? 's' : ''
                }`}</span>
              }
            />
          </div>
        )}

        {Object.entries(initialTradingGames).map(
          ([gameIdWithDuration, gameData]) => {
            // looping through games to render teams within, sharing game info
            const sampleGamePlayer = Object.values(gameData)[0];
            const [sampleGameProjection] = Object.values(sampleGamePlayer)[0];
            const { gameId: projectionGameId, startTime } =
              sampleGameProjection;

            const gameInfo = games.find(
              (game) => game.game_id === projectionGameId
            );

            const { standardStartTimes, uniqueStartTimes } =
              getTradingGameStartTimes({ gameData });

            // loop through team elements if team projections exist
            const teamElements = Object.entries(gameData).map(
              ([teamName, teamData]) => {
                const { primaryHeader, secondaryHeader } = determineTeamHeaders(
                  {
                    gameData,
                    gameInfo,
                    teamName
                  }
                );

                const awayTeam = gameInfo?.away_team || '';

                return (
                  <TradingTeam
                    bulkLeagueIdGroups={bulkLeagueIdGroups}
                    changes={changes}
                    leagueInfo={leagueInfo}
                    key={`${gameIdWithDuration}-${teamName}`}
                    gameId={projectionGameId}
                    gameStartTime={startTime}
                    awayTeam={awayTeam}
                    handleShowAutoTradeDrawer={handleShowAutoTradeDrawer}
                    handleSelectPlayerModal={handleSelectPlayerModal}
                    gameIdWithDuration={gameIdWithDuration}
                    players={teamData}
                    primaryHeader={primaryHeader}
                    secondaryHeader={secondaryHeader}
                    setConfirmationModalProps={setConfirmationModalProps}
                    standardStartTimes={standardStartTimes}
                    uniqueStartTimes={uniqueStartTimes}
                    updateProjection={updateProjection}
                    updateConfirmationModalProp={updateConfirmationModalProp}
                    isDisplayTimeVisible={isDisplayTimeVisible}
                  />
                );
              }
            );

            return teamElements;
          }
        )}
      </div>
      {isPublishProjectionsError && (
        <MessageBanner
          code="ERROR"
          message={errorMessage}
          onCancel={closeErrorMessage}
        />
      )}
    </>
  );
};
